import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, Clock } from 'lucide-react';

const FONT_D = { fontFamily: 'Playfair Display, serif' };
const FONT_B = { fontFamily: 'Manrope, sans-serif' };
const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p || 0);

const STATUS_CHIP = {
  booked: 'text-emerald-400 bg-emerald-400/10',
  invoice_sent: 'text-[#D4A843] bg-[#D4A843]/10',
  completed: 'text-white/40 bg-white/5',
};

function EventCard({ e, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                className="bg-[#141414] border border-white/[0.06] rounded-2xl p-5" data-testid={`vendor-event-${e.request_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="text-center w-11 flex-shrink-0">
            <p className="text-[#D4A843] text-xl font-bold leading-none tabular-nums">{e.event_date.slice(8, 10)}</p>
            <p className="text-white/30 text-[10px] uppercase tracking-wide mt-0.5">
              {new Date(e.event_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short' })}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-white text-[16px] truncate" style={FONT_D}>{e.event_type || 'Event'}{e.client_first_name ? ` — ${e.client_first_name}` : ''}</p>
            <p className="text-white/40 text-xs mt-0.5" style={FONT_B}>
              {e.booking_status === 'booked' ? 'Confirmed' : e.booking_status === 'completed' ? 'Completed' : 'Awaiting client payment'}
            </p>
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wide px-2.5 py-1 rounded-full flex-shrink-0 ${STATUS_CHIP[e.booking_status] || 'text-white/40 bg-white/5'}`} style={FONT_B}>
          {e.booking_status === 'invoice_sent' ? 'Pending' : e.booking_status}
        </span>
      </div>

      <div className="space-y-1.5 text-xs text-white/50 mb-4" style={FONT_B}>
        {e.event_time && <p className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-white/25 flex-shrink-0" strokeWidth={1.5} />{e.event_time}</p>}
        {e.event_location && <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-white/25 flex-shrink-0" strokeWidth={1.5} />{e.event_location}</p>}
        {e.guest_count && <p className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-white/25 flex-shrink-0" strokeWidth={1.5} />{e.guest_count} guests</p>}
      </div>

      <div className="space-y-1 mb-4">
        {e.items.map((i, idx) => (
          <p key={idx} className="text-white/70 text-sm" style={FONT_B}>
            {i.name}{i.quantity > 1 ? ` x${i.quantity}` : ''}{i.hours > 1 ? ` · ${i.hours}hrs` : ''}
          </p>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3.5 border-t border-white/[0.06]">
        <div>
          <p className="text-white/30 text-[10px] uppercase tracking-[0.2em]" style={FONT_B}>Your Payout</p>
          <p className="text-[#D4A843] text-lg font-semibold tabular-nums" style={FONT_B} data-testid={`payout-${e.request_id}`}>{fmt(e.payout)}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-wide px-3 py-1.5 rounded-full font-semibold ${
          e.payout_status === 'paid' ? 'text-emerald-400 bg-emerald-400/10' : 'text-amber-400 bg-amber-400/10'
        }`} style={FONT_B} data-testid={`payout-status-${e.request_id}`}>
          {e.payout_status === 'paid' ? 'Paid' : 'Payout Pending'}
        </span>
      </div>
    </motion.div>
  );
}

export const VendorEvents = () => {
  const [events, setEvents] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get('/api/vendor/events');
      setEvents(data.events);
    } catch (err) {
      console.error('Failed to load vendor events', err);
      setEvents([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (events === null) return <p className="text-neutral-600 text-sm py-16 text-center" style={FONT_B}>Loading events…</p>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.event_date >= today);
  const past = events.filter(e => e.event_date < today).reverse();
  const pendingPayout = events.filter(e => e.payout_status !== 'paid').reduce((s, e) => s + e.payout, 0);

  return (
    <div className="max-w-lg mx-auto px-6 pb-28" data-testid="vendor-events">
      <div className="pt-14 pb-6">
        <p className="text-neutral-500 text-sm font-medium mb-1" style={FONT_B}>Your schedule</p>
        <h1 className="text-white text-3xl font-medium tracking-tight" style={FONT_D}>My Events</h1>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-8" data-testid="vendor-events-stats">
        <div className="bg-[#141414] border border-white/[0.06] rounded-2xl py-4 text-center">
          <p className="text-white text-2xl tabular-nums" style={FONT_D}>{upcoming.length}</p>
          <p className="text-white/35 text-[10px] uppercase tracking-[0.2em] mt-1" style={FONT_B}>Upcoming</p>
        </div>
        <div className="bg-[#141414] border border-white/[0.06] rounded-2xl py-4 text-center">
          <p className="text-[#D4A843] text-2xl tabular-nums" style={FONT_D}>{fmt(pendingPayout)}</p>
          <p className="text-white/35 text-[10px] uppercase tracking-[0.2em] mt-1" style={FONT_B}>Payout Owed</p>
        </div>
      </div>

      {upcoming.length === 0 && past.length === 0 && (
        <div className="flex flex-col items-center py-16">
          <div className="w-16 h-16 rounded-full bg-white/[0.02] flex items-center justify-center mb-4">
            <Calendar className="w-7 h-7 text-neutral-800" strokeWidth={1.5} />
          </div>
          <p className="text-neutral-600 text-sm" style={FONT_B}>No events yet — approved requests will appear here</p>
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="mb-10">
          <p className="text-white/40 text-xs uppercase tracking-[0.2em] mb-4" style={FONT_B}>Upcoming</p>
          <div className="space-y-4">{upcoming.map((e, i) => <EventCard key={e.request_id} e={e} index={i} />)}</div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <p className="text-white/25 text-xs uppercase tracking-[0.2em] mb-4" style={FONT_B}>Past</p>
          <div className="space-y-4 opacity-60">{past.slice(0, 10).map((e, i) => <EventCard key={e.request_id} e={e} index={i} />)}</div>
        </div>
      )}
    </div>
  );
};
